import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { getLocalizedApiMessage } from "../../../utils/apiMessageHelper";
import {
  createUserRequest,
  getRoles,
  getUsers,
  removeUser,
  updateUserRequest,
} from "../../../services/api/users/usersApi";

export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async (dateRange = {}, { rejectWithValue }) => {
    try {
      const response = await getUsers(dateRange);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch users");
    }
  },
);

export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (userId, { rejectWithValue }) => {
    try {
      await removeUser(userId);
      return userId;
    } catch (error) {
      return rejectWithValue("Failed to delete user");
    }
  },
);

export const addUser = createAsyncThunk(
  "users/addUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await createUserRequest(userData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, "Failed to add user"),
      );
    }
  },
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ userId, userData }, { rejectWithValue }) => {
    try {
      const response = await updateUserRequest(userId, userData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, "Failed to update user"),
      );
    }
  },
);

export const fetchRoles = createAsyncThunk(
  "users/fetchRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRoles();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch roles");
    }
  },
);

const getLang = () =>
  localStorage.getItem("language") === "ar" ? "ar" : "en";

const usersSlice = createSlice({
  name: "users",
  initialState: {
    userData: [],
    userCounts: null,
    roles: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.userData = action.payload.users;
        state.userCounts = action.payload.counts;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.userData = state.userData.filter(
          (user) => user.id !== action.payload,
        );
        const toastMessages = {
          en: "User deleted successfully!",
          ar: "تم حذف المستخدم بنجاح!",
        };
        toast.success(toastMessages[getLang()]);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload;
        const toastMessages = {
          en: "Failed to delete user.",
          ar: "فشل في حذف المستخدم.",
        };
        toast.error(toastMessages[getLang()]);
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.userData.push(action.payload);
        const toastMessages = {
          en: "User created successfully!",
          ar: "تم إنشاء المستخدم بنجاح!",
        };
        toast.success(toastMessages[getLang()]);
      })
      .addCase(addUser.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(action.payload || "Failed to create user.");
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const index = state.userData.findIndex(
          (user) => user.id === action.payload.id,
        );
        if (index !== -1) {
          state.userData[index] = action.payload;
        }
        const toastMessages = {
          en: "User updated successfully!",
          ar: "تم تحديث المستخدم بنجاح!",
        };
        toast.success(toastMessages[getLang()]);
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(action.payload || "Failed to update user.");
      })
      .addCase(fetchRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = action.payload;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default usersSlice.reducer;
